/* eslint-disable react-refresh/only-export-components */
import { DeleteIcon } from "@/components/icons/action-icons";
import DynamicField from "@/components/DynamicField";
import { Loader2 } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Formik } from "formik";
import * as Yup from "yup";
import type { FieldConfig } from "@/utils/formTypes";

type ConfirmFn = (options: {
  title: string;
  msg: string;
  onConfirm: (values?: Record<string, any>) => Promise<void>;
  onCancel?: () => void;
  icon?: React.ReactNode;
  color?: string;
  extraFields?: FieldConfig[];
  /**
   * Action button text. Defaults to a translated "Confirm", which is fine for a delete
   * prompt and ambiguous everywhere else — on "Discard unsaved changes?" it
   * gives no clue whether confirming discards or stays. Name the action.
   */
  confirmLabel?: string;
  /** Defaults to a translated "Cancel". */
  cancelLabel?: string;
}) => void;

const ConfirmContext = createContext<ConfirmFn>(() => {});

// Helper to build validation schema from field configs
const buildValidationSchema = (fields?: FieldConfig[]) => {
  if (!fields?.length) return Yup.object({});

  const shape: Record<string, Yup.AnySchema> = {};
  fields.forEach((field) => {
    if (field.validation) {
      shape[field.name] = field.validation;
    }
  });
  return Yup.object(shape);
};

// Helper to build initial values from field configs
const buildInitialValues = (fields?: FieldConfig[]) => {
  if (!fields?.length) return {};
  return fields.reduce(
    (acc, field) => {
      acc[field.name] = "";
      return acc;
    },
    {} as Record<string, any>,
  );
};

export const ConfirmProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation(["common"]);
  const [state, setState] = useState<{
    title: string;
    msg: string;
    icon?: React.ReactNode;
    color?: string;
    extraFields?: FieldConfig[];
    confirmLabel?: string;
    cancelLabel?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onConfirmRef =
    useRef<(values?: Record<string, any>) => Promise<void>>(null);
  const onCancelRef = useRef<(() => void) | undefined>(undefined);

  const confirm: ConfirmFn = useCallback(
    ({
      title,
      msg,
      onConfirm,
      onCancel,
      icon,
      color = "var(--color-danger)",
      extraFields,
      confirmLabel,
      cancelLabel,
    }) => {
      setState({ title, msg, icon, color, extraFields, confirmLabel, cancelLabel });
      onConfirmRef.current = onConfirm;
      onCancelRef.current = onCancel;
    },
    [],
  );

  const handleConfirm = async (values?: Record<string, any>) => {
    if (!onConfirmRef.current) return;
    try {
      setIsLoading(true);
      await onConfirmRef.current(values);
      setState(null);
    } catch (error: any) {
      toast.error(
        error?.data?.message ??
          error?.message ??
          t("common:states.error_title"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    onCancelRef.current?.();
    setState(null);
  }, []);

  /**
   * Escape is how people leave a modal. Without this the only exits were the
   * two buttons — and a caller relying on `onCancel` to unwind its own state
   * had no way to be told the user backed out.
   *
   * CAPTURE PHASE, and the event stops here. A Radix `Dialog` listens for
   * Escape on `document`, which bubbles BEFORE a `window` listener — so a
   * confirm raised from inside a dialog had one Escape close both of them,
   * dismissing the dialog the user was only trying to back out of a prompt
   * from. Capturing at `window` runs first, and stopping propagation keeps the
   * keypress from reaching the layer underneath.
   */
  useEffect(() => {
    if (!state) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || isLoading) return;
      e.stopPropagation();
      handleCancel();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [state, isLoading, handleCancel]);

  const hasExtraFields = Boolean(state?.extraFields?.length);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state &&
        createPortal(
          <div
            /*
             * `pointer-events-auto` is load-bearing, not decoration.
             *
             * A Radix modal Dialog sets `pointer-events: none` on <body> for as
             * long as it is open, so only its own subtree stays clickable. This
             * confirm renders through a SEPARATE portal, also under <body>, so
             * it inherited that and painted perfectly while ignoring every
             * click — buttons visible, nothing happening. Any confirm raised
             * from inside a dialog hit this.
             */
            /*
             * `p-4` and `overflow-y-auto` are the phone case.
             *
             * The card below is `max-w-sm w-full`, so on a 375px screen it
             * filled the viewport edge to edge with no gutter — and a prompt
             * carrying `extraFields` on a short screen overflowed a centred
             * flex container with no way to scroll to its buttons.
             */
            className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto p-4 z-50 pointer-events-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-msg"
          >
            <Formik
              initialValues={buildInitialValues(state.extraFields)}
              validationSchema={buildValidationSchema(state.extraFields)}
              onSubmit={handleConfirm}
            >
              {({ values, errors, touched, setFieldValue, handleSubmit }) => (
                <div className="bg-surface p-6 rounded-sm shadow-xl max-w-sm w-full my-auto">
                  <div
                    className="flex items-center justify-center h-16 w-16 rounded-full mx-auto mb-4"
                    style={{
                      /*
                       * `color-mix`, not `${color}33`: appending alpha digits
                       * only works for a hex literal. A caller passing a design
                       * token — `var(--color-warning)` — produced
                       * `var(--color-warning)33`, which is not a colour, so the
                       * tint silently vanished while the icon itself was tinted
                       * correctly. This accepts both forms.
                       */
                      backgroundColor: `color-mix(in oklab, ${state.color} 20%, transparent)`,
                      color: state.color,
                    }}
                  >
                    {state.icon ?? <DeleteIcon />}
                  </div>
                  <h3
                    id="confirm-dialog-title"
                    className="text-lg font-medium text-center"
                    style={{ color: state.color }}
                  >
                    {state.title}
                  </h3>
                  <p
                    id="confirm-dialog-msg"
                    className="py-3 text-muted-foreground text-center"
                  >
                    {state.msg}
                  </p>

                  {hasExtraFields && (
                    <div className="space-y-3 mb-4">
                      {state.extraFields?.map((field) => (
                        <DynamicField
                          key={field.name}
                          name={field.name}
                          field={field}
                          value={values[field.name]}
                          onChange={(value) => setFieldValue(field.name, value)}
                          error={
                            touched[field.name] ? errors[field.name] : undefined
                          }
                          touched={touched[field.name]}
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      autoFocus
                      disabled={isLoading}
                      onClick={handleCancel}
                      className="px-4 py-2 border border-border rounded-xs disabled:opacity-50 grow text-center text-border-strong"
                    >
                      {state.cancelLabel ?? t("common:buttons.cancel")}
                    </button>
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() =>
                        hasExtraFields ? handleSubmit() : handleConfirm()
                      }
                      className="px-4 py-2 text-primary-foreground rounded-xs flex items-center justify-center gap-2 grow text-center"
                      style={{ backgroundColor: state.color }}
                    >
                      {isLoading && (
                        <Loader2 size={20} className="animate-spin" />
                      )}
                      {state.confirmLabel ?? t("common:buttons.confirm")}
                    </button>
                  </div>
                </div>
              )}
            </Formik>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
